import React from 'react';
import { Link } from 'react-router-dom';
import { Ora } from '../../../../assets/svgs/Ora';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { useCapability, useInstance } from '../../../../instances/useInstance';
import { INSTANCE_SLUGS } from '../../../../instances/instanceSlugs';
import { cn } from '../../../../lib/utils';

interface OraLogoProps {
  type?: string;
  url?: string;
  clickFunction?: () => void;
  ariaLabel?: string;
  showCoBrand?: boolean;
  /**
   * Which of a replacing instance's logos to draw: its mark (the icon rail)
   * or its full wordmark (the expanded rail). Falls back to the mark when
   * the instance has no wordmark; co-branding never changes.
   */
  variant?: 'mark' | 'wordmark';
}

interface LogoContentProps {
  instanceSlug?: string;
  logoUrl: string | null;
  wordmarkUrl: string | null;
  variant: 'mark' | 'wordmark';
  displayName: string;
  showCoBrand: boolean;
  /**
   * An instance with `brandMark: 'replace'` wears its own mark in place of
   * Ora's, wherever the logo is drawn; every other instance co-brands beside
   * it. The instance's `logoUrl` is the one source of the mark either way.
   */
  replacesOra: boolean;
}

// Without a partner logo this renders the bare Ora mark, exactly as before
// instances existed, so the Default instance's layout is unchanged. Exported
// for the pre-auth LMS Connect landing, which brands from its launch rather
// than the signed-in profile (ORA-1077).
export const LogoContent: React.FC<LogoContentProps> = ({
  instanceSlug,
  logoUrl,
  wordmarkUrl,
  variant,
  displayName,
  showCoBrand,
  replacesOra,
}) => {
  const [imageFailed, setImageFailed] = React.useState(false);

  React.useEffect(() => {
    setImageFailed(false);
  }, [logoUrl]);

  if (logoUrl && replacesOra && !imageFailed) {
    if (variant === 'wordmark' && wordmarkUrl) {
      // The wordmark is the mark plus text from the same origin, so its
      // left inset is the slack the mark has centred in its 2.25rem box
      // (about 4px at this height): the shield holds still on hover.
      return (
        <span className="inline-flex pl-1">
          <img
            src={wordmarkUrl}
            alt={displayName}
            className="h-8 w-auto max-w-40 object-contain"
            data-testid="instance-wordmark"
            onError={() => setImageFailed(true)}
          />
        </span>
      );
    }

    // In the box the Ora mark takes (the nav sizes it 2.25rem wide), so the
    // mark sits centred over the icon column below it.
    return (
      <span className="inline-flex w-9 justify-center">
        <img
          src={logoUrl}
          alt={displayName}
          className="h-8 w-auto max-w-24 object-contain"
          data-testid="instance-logo"
          onError={() => setImageFailed(true)}
        />
      </span>
    );
  }

  if (!logoUrl || !showCoBrand || imageFailed) return <Ora />;

  return (
    <span className="inline-flex items-center gap-2">
      <Ora />
      <span aria-hidden="true" className="h-4 w-px shrink-0 bg-border-subtle" />
      <img
        src={logoUrl}
        alt={displayName}
        className={cn(
          'w-auto max-w-24 object-contain',
          // BYU's official navy lockup needs more space and a white version
          // against the dark rail. Filtering preserves the original shapes.
          instanceSlug === INSTANCE_SLUGS.byu
            ? 'h-8 dark:brightness-0 dark:invert'
            : 'h-4'
        )}
        onError={() => setImageFailed(true)}
      />
    </span>
  );
};

const OraLogo: React.FC<OraLogoProps> = ({
  type,
  url,
  clickFunction,
  ariaLabel = 'Ora AI home',
  showCoBrand = true,
  variant = 'mark',
}) => {
  const instance = useInstance();
  const replacesOra = useCapability('brandMark') === 'replace';

  const logoContent = (
    <LogoContent
      instanceSlug={instance.slug}
      logoUrl={instance.brand.logoUrl}
      wordmarkUrl={instance.brand.wordmarkUrl}
      variant={variant}
      displayName={instance.displayName}
      showCoBrand={showCoBrand}
      replacesOra={replacesOra}
    />
  );

  return (
    <div className="logo">
      {type === 'link' &&
        // An in-app path routes client-side (the desktop closes the window
        // in place); an external URL keeps the plain anchor.
        (url?.startsWith('/') ? (
          <Link to={url} aria-label={ariaLabel}>
            {logoContent}
          </Link>
        ) : (
          <a href={url} aria-label={ariaLabel}>
            {logoContent}
          </a>
        ))}
      {type === 'button' && (
        <button
          aria-label={ariaLabel}
          onClick={() => {
            hapticsImpactLight();
            if (clickFunction) {
              clickFunction();
            }
          }}
        >
          {logoContent}
        </button>
      )}
      {!type && logoContent}
    </div>
  );
};

export default OraLogo;
